/**
 * MyWork — /w/:slug/app/mine
 *
 * PR-P1-3b (ADR-0009 frontend). Register items assigned to the current
 * signed-in workspace member, ordered by priority. Mirrors the "assigned
 * to me" list pattern already shipped for MyEvidenceRequests.tsx (same
 * severity-coded card-list + count-tile strip shape).
 *
 * Reduced scope vs the UC-3 R2 mockup
 * (.claude/skills/ux-adversary/runs/uc-3-work-priorities/worker-my-work.web.html):
 * that mockup groups items by "To do / Awaiting reply / Done today",
 * shows deadlines, "waiting on X", "blocks N", live-check freshness, and
 * nudge affordances. None of that data exists yet — due dates
 * (ADR-0009 bullet #2), the notification system (bullet #3), and
 * "blocks" relationships are all out of PR-P1-3a's shipped backend
 * scope (tracked as P1-3a-followup-1/2 in
 * docs/implementation-plan.md Appendix F). This page renders only
 * what GET /register/mine actually returns: title, severity, status,
 * category, priority — a flat list ordered by priority_weight DESC,
 * since every row here is already "to do" (todo | in_progress | blocked | review_needed).
 *
 * ux-adversary critique pass (2026-08-27, Marcus persona + aesthetic +
 * flow critics) fixed in this PR: severity-coded left border + count
 * tiles (aesthetic High — priority was invisible), priority_weight
 * chip per row (persona Medium — ranking was a black box), empty-state
 * self-assign copy gated on canManage (flow Medium — viewers saw
 * instructions that don't apply to them), EmptyState action button
 * (persona Nit). See docs/implementation-plan.md §2.4 deviation notes
 * for what was deferred vs fixed.
 *
 * PR-P2-4 adds the "Questionnaires" rollup section below (per-question
 * "Distribute" assignments, rolled up one row per questionnaire — see
 * questionnaire-assignment-service.ts's getMyQuestionnaireRollups doc
 * comment). ux-adversary flow critic Blocker fixed: `mine` and
 * `rollupState` are two independent fetches; gating the loading spinner
 * on only one of them meant the page could flash the "nothing assigned"
 * empty state, then flip to show questionnaires a moment later once the
 * second fetch caught up — and a failed rollup fetch rendered as if
 * there were simply zero questionnaires, hiding real work. Both are now
 * awaited together and both surface their own error state.
 *
 * Nadia persona critic Blocker (deferred, not fixed — see
 * docs/implementation-plan.md Appendix F): this page is workspace-scoped
 * (`/api/w/:slug/...`), same as the register-items half already was
 * before this PR. A consultant across several client workspaces gets no
 * cross-workspace view here — they still have to check each workspace's
 * /mine separately. Real cross-workspace aggregation is an app-wide
 * session/routing change (workspace-scoping is a deliberate security
 * boundary per CLAUDE.md, not an oversight), out of this PR's file scope
 * to fix as a side effect of adding the questionnaire rollup.
 */

import { Link } from 'react-router-dom';
import type { MyWorkItem, Severity } from '@aigovops/core';
import {
  Alert,
  Button,
  EmptyState,
  PageHeader,
  SeverityBadge,
  Spinner,
  StatusBadge,
} from '@aigovops/design-system';
import { useOptionalWorkspace } from '../lib/workspace';
import { useWorkspaceQuery } from '../hooks/useWorkspaceQuery';
import { queryKeys } from '../lib/query-keys';
import { getMyWork, getMyQuestionnaireRollups, getEvidenceGaps } from '../lib/api';
import { categoryLabel } from '../lib/format';
import { PendingPoliciesRow } from '../components/PendingPolicies';

export function MyWork() {
  const workspaceCtx = useOptionalWorkspace();
  const slug = workspaceCtx?.workspace.slug ?? null;
  const prefix = workspaceCtx ? `/w/${workspaceCtx.workspace.slug}/app` : '/app'; // scope-check-ignore: legacy fallback
  const canManage =
    workspaceCtx?.role === 'owner' ||
    workspaceCtx?.role === 'admin' ||
    workspaceCtx?.role === 'editor';

  const mine = useWorkspaceQuery(
    (slug) => queryKeys.myWork(slug),
    (slug) => getMyWork(slug),
  );
  // PR-P2-4: questionnaire "Distribute" rollup — one row per questionnaire
  // with open questions assigned to the caller, not one row per question
  // (see questionnaire-assignment-service.ts's getMyQuestionnaireRollups
  // doc comment for why: the uc-5-questionnaires mockup's own design-
  // rationale flags a 40-row-per-questionnaire queue as the failure mode
  // a flat per-question list would hit).
  const rollupState = useWorkspaceQuery(
    (slug) => queryKeys.myQuestionnaireRollups(slug),
    (slug) => getMyQuestionnaireRollups(slug),
  );
  const gapState = useWorkspaceQuery(
    (slug) => queryKeys.evidenceGaps(slug),
    (slug) => getEvidenceGaps(slug),
  );

  if (!slug) return null;
  if (mine.loading || rollupState.loading || gapState.loading) return <Spinner label="Loading your work" />;
  if (mine.error) return <Alert variant="error">{mine.error}</Alert>;
  if (rollupState.error) return <Alert variant="error">{rollupState.error}</Alert>;

  const items = mine.data?.items ?? [];
  const rollups = rollupState.data?.rollups ?? [];
  const gaps = gapState.data?.gaps ?? [];
  const criticalCount = items.filter((i) => i.severity === 'critical').length;
  const highCount = items.filter((i) => i.severity === 'high').length;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-5)', maxWidth: '68rem' }}>
      <PageHeader
        title="My work"
        subtitle={
          items.length > 0 || rollups.length > 0
            ? `${items.length} register item${items.length === 1 ? '' : 's'} · ${rollups.length} questionnaire${rollups.length === 1 ? '' : 's'} with questions assigned to you`
            : 'Register items and questionnaire questions assigned to you.'
        }
      />

      {/* V96-4-9: policies waiting for this member's acknowledgment. */}
      <PendingPoliciesRow />

      {items.length === 0 && rollups.length === 0 ? (
        <EmptyState
          title="Nothing assigned to you"
          description={
            canManage
              ? 'Register items and questionnaire questions appear here once a teammate assigns them to you. You can also assign yourself an item from its detail page.'
              : 'Register items and questionnaire questions appear here once an editor or admin assigns one to you.'
          }
          action={
            <Link to={`${prefix}/register`} style={{ textDecoration: 'none' }}>
              <Button type="button">Browse register</Button>
            </Link>
          }
        />
      ) : (
        <>
          {items.length > 0 ? <ScoreboardStrip total={items.length} critical={criticalCount} high={highCount} /> : null}
          {rollups.length > 0 ? (
            <section aria-label="Questionnaire questions assigned to you" style={{ display: 'grid', gap: 'var(--space-2)' }}>
              <h2 style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--text)' }}>Questionnaires</h2>
              <ol style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 'var(--space-2)' }}>
                {rollups.map((r) => (
                  <li key={r.questionnaire_id}>
                    <Link
                      to={`${prefix}/questionnaires/${encodeURIComponent(r.questionnaire_id)}?assigned=me`}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 'var(--space-3)',
                        padding: 'var(--space-3) var(--space-4)',
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--radius-card)',
                        background: 'var(--surface)',
                        color: 'var(--text)',
                        textDecoration: 'none',
                      }}
                    >
                      <span style={{ fontWeight: 500 }}>{r.title}</span>
                      <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-3xs)' }}>
                        {r.assigned_count} question{r.assigned_count === 1 ? '' : 's'} assigned to you →
                      </span>
                    </Link>
                  </li>
                ))}
              </ol>
            </section>
          ) : null}
          {gaps.length > 0 ? (
            <section aria-label="Attestation gaps" style={{ display: 'grid', gap: 'var(--space-2)' }}>
              <h2 style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--text)' }}>Attestation gaps</h2>
              <p style={{ margin: 0, fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)' }}>
                Register items cited in questionnaires but lacking evidence. Upload documentation to strengthen responses.
              </p>
              <ol style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 'var(--space-2)' }}>
                {gaps.map((g) => (
                  <li key={g.risk_item_id}>
                    <Link
                      to={`${prefix}/evidence?item=${encodeURIComponent(g.risk_item_id)}`}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 'var(--space-3)',
                        padding: 'var(--space-3) var(--space-4)',
                        border: '1px solid var(--border)',
                        borderLeftWidth: '3px',
                        borderLeftColor: 'var(--warning-fg)',
                        borderRadius: 'var(--radius-card)',
                        background: 'var(--surface)',
                        color: 'var(--text)',
                        textDecoration: 'none',
                      }}
                    >
                      <span style={{ fontWeight: 500 }}>{g.title}</span>
                      <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-3xs)', whiteSpace: 'nowrap' }}>
                        {g.citation_count} citation{g.citation_count === 1 ? '' : 's'} across {g.questionnaire_count} questionnaire{g.questionnaire_count === 1 ? '' : 's'}
                      </span>
                    </Link>
                  </li>
                ))}
              </ol>
            </section>
          ) : null}
          {items.length > 0 ? (
            <ol
              style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                display: 'grid',
                gap: 'var(--space-3)',
              }}
            >
              {items.map((item) => (
                <MyWorkRow key={item.register_item_id} item={item} appPrefix={prefix} />
              ))}
            </ol>
          ) : null}
        </>
      )}
    </div>
  );
}

// ---- Scoreboard strip (mirrors MyEvidenceRequests.tsx's Tile pattern) --

function ScoreboardStrip({
  total,
  critical,
  high,
}: {
  total: number;
  critical: number;
  high: number;
}) {
  return (
    <section
      aria-label="Work summary"
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(9rem, 1fr))',
        gap: 'var(--space-3)',
      }}
    >
      <Tile label="Total assigned" value={total} tone="muted" />
      <Tile label="Critical" value={critical} tone={critical > 0 ? 'danger' : 'muted'} />
      <Tile label="High" value={high} tone={high > 0 ? 'warning' : 'muted'} />
    </section>
  );
}

function Tile(props: { label: string; value: number; tone: 'muted' | 'warning' | 'danger' }) {
  const toneStyle: Record<typeof props.tone, { bg: string; border: string; label: string }> = {
    muted: { bg: 'var(--surface)', border: 'var(--border)', label: 'var(--text-muted)' },
    warning: { bg: 'var(--warning-bg)', border: 'var(--band-amber)', label: 'var(--warning-fg)' },
    danger: { bg: 'var(--danger-bg)', border: 'var(--band-red)', label: 'var(--danger-fg)' },
  };
  const t = toneStyle[props.tone];
  return (
    <div
      style={{
        padding: 'var(--space-3) var(--space-4)',
        border: `1px solid ${t.border}`,
        borderRadius: 'var(--radius-card)',
        background: t.bg,
        display: 'grid',
        gap: '2px',
      }}
    >
      <span
        style={{
          fontSize: 'var(--font-size-3xs)',
          color: t.label,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          fontWeight: 600,
        }}
      >
        {props.label}
      </span>
      <span
        style={{
          fontSize: 'var(--font-size-xl)',
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
          color: 'var(--text)',
        }}
      >
        {props.value}
      </span>
    </div>
  );
}

// ---- Row ---------------------------------------------------------------

function severityBorder(severity: Severity): string {
  if (severity === 'critical') return 'var(--band-red)';
  if (severity === 'high' || severity === 'elevated') return 'var(--band-amber)';
  return 'var(--border)';
}

function MyWorkRow({ item, appPrefix }: { item: MyWorkItem; appPrefix: string }) {
  return (
    <li>
      <Link
        to={`${appPrefix}/register/${encodeURIComponent(item.register_item_id)}`}
        style={{
          display: 'grid',
          gap: 'var(--space-2)',
          padding: 'var(--space-3) var(--space-4)',
          border: '1px solid var(--border)',
          borderLeftWidth: '3px',
          borderLeftColor: severityBorder(item.severity),
          borderRadius: 'var(--radius-card)',
          background: 'var(--surface)',
          color: 'inherit',
          textDecoration: 'none',
        }}
      >
        <header style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'baseline', flexWrap: 'wrap' }}>
          <SeverityBadge severity={item.severity} />
          <StatusBadge status={item.status} />
          <span style={{ flex: 1, minWidth: 0, fontWeight: 500, color: 'var(--text)' }}>
            {item.title}
          </span>
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--font-size-3xs)',
              color: 'var(--text-dim)',
            }}
            title="Priority weight"
          >
            P{item.priority_weight}
          </span>
        </header>
        <footer
          style={{
            display: 'flex',
            gap: 'var(--space-2)',
            color: 'var(--text-dim)',
            fontSize: 'var(--font-size-3xs)',
          }}
        >
          <span>{categoryLabel[item.category]}</span>
        </footer>
      </Link>
    </li>
  );
}
